import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useFilterNames } from '@/api/common';
import TooltipRow from '@/components/Flare/guides/TooltipRow';
import TooltipTitle from '@/components/Flare/guides/TooltipTitle';
import { useInstantSearchState } from '@/components/InstantSearch/instantSearchContext';
import { type QueryStateFilterOperator } from '@/components/InstantSearch/types';
import { isFilterRange } from '@/components/InstantSearch/util/filter-util';
import { SpinnerOverlay } from '@/components/loaders';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Link } from '@/components/typography';
import { cn } from '@/lib/utils';

type Props = {
  className?: string;
  getFieldName?: (fieldId: string) => string;
};

const FilterDetails = ({ className, getFieldName }: Props) => {
  const {
    queryState: { filters },
    updateQueryState,
  } = useInstantSearchState();

  const { filtersWithNames, isLoading, error } = useFilterNames(filters);

  // Don't render one if there are no filters
  if (!filters?.length) {
    return null;
  }

  const handleFiltersClear = () => {
    updateQueryState({ filters: [], page: 1 });
  };

  const getFilterText = (operator: QueryStateFilterOperator, operand: string) => {
    if (operator === 'notIn') {
      return t`NOT ${operand}`;
    }
    return operand;
  };

  return (
    <div className={cn('flex gap-4', className)}>
      <Link variant="caption1" onClick={handleFiltersClear}>
        <Trans>Clear Filters</Trans>
      </Link>
      <Popover modal={false}>
        <PopoverTrigger
          nativeButton={false}
          render={
            <Link variant="caption1">
              <Trans>Filter Details ({filters.length})</Trans>
            </Link>
          }
        />
        <PopoverContent side="left" className="dark w-auto max-w-115">
          <table data-testid="filter-details-popover-content">
            <thead>
              <TooltipTitle>
                <Trans>View Currently Filtered by:</Trans>
              </TooltipTitle>
            </thead>
            <tbody>
              {isLoading && <SpinnerOverlay isSpinning />}
              {!isLoading &&
                (error ? filters : filtersWithNames).map((filter) => {
                  let filterValueText = '';
                  if (isFilterRange(filter)) {
                    if (filter.min) {
                      filterValueText = filter.min.inclusive ? '>=' : '>';
                      filterValueText += ` ${filter.min.operand}`;
                    }
                    if (filter.max) {
                      if (filterValueText.length) {
                        filterValueText += '<br/>';
                      }
                      filterValueText += filter.max.inclusive ? '<=' : '<';
                      filterValueText += ` ${filter.max.operand}`;
                    }
                  } else {
                    if (Array.isArray(filter.operand)) {
                      const booleanOperator = filter.booleanOperator ?? 'OR';
                      filterValueText = filter.operand
                        .map((operand) => getFilterText(filter.operator, operand))
                        .join(`${booleanOperator === 'OR' ? '' : t` AND`}<br/>`);
                    } else {
                      filterValueText = getFilterText(filter.operator, filter.operand);
                    }
                  }
                  return (
                    <TooltipRow
                      key={filter.field}
                      name={getFieldName?.(filter.field) ?? filter.field}
                      value={filterValueText}
                      valueOverflow
                    />
                  );
                })}
            </tbody>
          </table>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FilterDetails;
